import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [expandedDesc, setExpandedDesc] = useState(false);
  const [activeTab, setActiveTab] = useState<'chapters' | 'comments'>('chapters');

  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
          <h1 className="text-2xl font-bold text-foreground-900">漫画未找到</h1>
          <p className="text-foreground-500 mt-2">抱歉，该漫画不存在或已下架。</p>
          <Link
            to="/"
            className="inline-block mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedComics = comics
    .filter(
      (c) => c.category === comic.category && c.id !== comic.id
    )
    .slice(0, 5);

  const mockChapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    id: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(
      new Date(comic.updatedAt).getTime() - i * 7 * 24 * 60 * 60 * 1000
    )
      .toISOString()
      .split('T')[0],
  }));

  const mockComments = [
    { id: 1, user: '漫画迷小王', rating: 5, content: '画风太棒了，剧情紧凑让人欲罢不能！完全停不下来，强烈推荐！', date: '2026-08-03' },
    { id: 2, user: '深夜追漫', rating: 5, content: '每天就等着更新，作者真的很会讲故事。人物形象塑造得特别丰满。', date: '2026-08-02' },
    { id: 3, user: '韩漫爱好者', rating: 4, content: '整体质量很高，就是更新速度能再快一点就好了...每周等得太煎熬。', date: '2026-08-01' },
    { id: 4, user: '新来的读者', rating: 5, content: '被朋友安利来看的，一口气追到最新话！这个作者的作品质量真的高。', date: '2026-07-30' },
    { id: 5, user: '老读者了', rating: 5, content: '追了一年多了，质量一直很稳定。这个作者真的每一部作品都值得看。', date: '2026-07-28' },
  ];

  function formatViews(views: number): string {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}万`;
    }
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 汗汗漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2025-01-01',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '汗汗漫画',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hanhancomic.com/comic/${comic.id}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${comic.categoryName}漫画`, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <main>
        {/* Breadcrumb + Background */}
        <section className="bg-background-100 pt-6 pb-0">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        {/* Comic Header */}
        <section className="bg-background-100 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Cover */}
              <div className="shrink-0 mx-auto md:mx-0">
                <div className="w-44 md:w-52 rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 汗汗漫画 免费韩国漫画在线阅读`}
                    className="w-full aspect-[3/4] object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-background-200 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  <Link
                    to={`/category/${comic.category}`}
                    className="inline-block px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors whitespace-nowrap"
                  >
                    {comic.categoryName}
                  </Link>
                  <span className="text-sm text-foreground-500">
                    作者：{comic.author}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-4">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-secondary-500"></i>
                    <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-500">分</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-eye-line"></i>
                    {formatViews(comic.views)} 次阅读
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-file-list-3-line"></i>
                    共 {comic.chapters} 话
                  </div>
                </div>

                {/* Tags */}
                {comic.tags && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-4">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 rounded-full bg-background-200/70 text-xs text-foreground-600"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Description */}
                <div className="mt-4">
                  <p className={`text-sm text-foreground-600 leading-relaxed ${
                    !expandedDesc ? 'line-clamp-2' : ''
                  }`}>
                    {comic.description}
                  </p>
                  <button
                    onClick={() => setExpandedDesc(!expandedDesc)}
                    className="text-sm text-primary-500 hover:text-primary-600 mt-1 font-medium transition-colors whitespace-nowrap"
                  >
                    {expandedDesc ? '收起' : '展开全部'}
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-3 mt-6">
                  <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-colors whitespace-nowrap text-sm">
                    开始阅读
                  </button>
                  <button className="px-6 py-2.5 bg-secondary-500 hover:bg-secondary-600 text-background-50 font-semibold rounded-full transition-colors whitespace-nowrap text-sm">
                    收藏漫画
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs: Chapters / Comments */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
              {/* Left: Tab Content */}
              <div className="flex-1">
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-6">
                  <button
                    onClick={() => setActiveTab('chapters')}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      activeTab === 'chapters'
                        ? 'bg-background-50 text-foreground-950 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    章节列表
                  </button>
                  <button
                    onClick={() => setActiveTab('comments')}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      activeTab === 'comments'
                        ? 'bg-background-50 text-foreground-950 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    读者评论
                  </button>
                </div>

                {activeTab === 'chapters' && (
                  <div className="space-y-2">
                    {mockChapters.map((ch) => (
                      <div
                        key={ch.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <i className="ri-book-read-line text-lg text-foreground-400"></i>
                          <span className="text-sm font-medium text-foreground-800">
                            {ch.title}
                          </span>
                        </div>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          {ch.date}
                        </span>
                      </div>
                    ))}
                    {comic.chapters > 20 && (
                      <div className="text-center pt-4">
                        <button className="px-6 py-2 border border-background-300 text-sm text-foreground-600 rounded-full hover:bg-background-100 transition-colors whitespace-nowrap">
                          查看更多章节（共{comic.chapters}话）
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'comments' && (
                  <div className="space-y-4">
                    {mockComments.map((comment) => (
                      <div
                        key={comment.id}
                        className="p-4 rounded-lg bg-background-100"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-sm font-bold">
                              {comment.user.charAt(0)}
                            </div>
                            <span className="text-sm font-medium text-foreground-800">
                              {comment.user}
                            </span>
                          </div>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: 5 }, (_, i) => (
                              <i
                                key={i}
                                className={`text-xs ${
                                  i < comment.rating
                                    ? 'ri-star-fill text-secondary-500'
                                    : 'ri-star-line text-foreground-300'
                                }`}
                              ></i>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {comment.content}
                        </p>
                        <span className="text-xs text-foreground-400 mt-2 block">
                          {comment.date}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Related Comics */}
              <div className="lg:w-72 shrink-0">
                <h4 className="text-lg font-bold text-foreground-950 mb-4">
                  相关推荐
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-4">
                  {relatedComics.map((related) => (
                    <Link
                      key={related.id}
                      to={`/comic/${related.id}`}
                      className="flex gap-3 group"
                    >
                      <div className="w-16 shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={related.cover}
                          alt={related.title}
                          className="w-full aspect-[3/4] object-cover transition-transform group-hover:scale-105"
                          title={`${related.title} - 汗汗漫画`}
                        />
                      </div>
                      <div className="min-w-0">
                        <h5 className="text-sm font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-500 transition-colors">
                          {related.title}
                        </h5>
                        <div className="flex items-center gap-2 mt-1 text-xs text-foreground-400">
                          <span>{related.categoryName}</span>
                          <span>{related.rating}分</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}